// Pinia Store
import { defineStore } from 'pinia'
import { getTranslation } from '../i18nlocal' // import the i18n instance
import { useAuthUserStore } from './auth';
import { useNotifications } from '@components/NotificationPlugin/index';
type LogMessage = {
  message: string;
  type: string;
  tStamp : Date;
};

type OrderRunState = {
  running: boolean;
  status: string;
};

interface Order {
  Id: string;
  messages: LogMessage[]; // replace with the type of your order
  running: OrderRunState;
  tStamp: Date;
  showDelete: boolean;
  passwordPresented: boolean;
  invoiceUrl: string;
}


export interface AppConfig {
  domain: string;
  hostname?: string;
  enabled?: boolean;
  auto_url?: string;
  broadcast_url?: string;
  firebaseConfig?: Record<string, string>;
  defaultcustomerId?: string;
  address?: string;
  backgroundImageUrl?: string;
  contactEmail?: string;
  contactPhone?: string;
}

export interface ServerCommandData {
  invoiceUrl?: string;
  url?: string;
  message?: string;
  message_type?: string;
  data?: Record<string, string | number | boolean>;
}

export interface ServerLogMessage {
  orderId?: string;
  command?: string;
  command_data?: ServerCommandData;
  // Flat server log shape (no command): message + optional data payload.
  data?: Record<string, string | number | boolean>;
  message_type?: string;
  message?: string;
}

interface State {
  myorders: Order[];
  mylocale: string
  myfallbackLocale: string
  _appConfig: AppConfig | null
  _appConfigTime: number | null
  _customerId: string | null
  myorderStarted: boolean
}

let storeInitialized = false;

export const useSessionStore = () => {
  const store = useSessionStoreLocal();

  if (!storeInitialized) {
    // Check if _appConfig is older than 1 minute and clear it if so
    const now = Date.now();
    if (store._appConfigTime)
    {
      if (now - store._appConfigTime > 60000) {
        store.setAppConfig(null);
      }
    }
    else
    {
      store.setAppConfig(null);
    }

    storeInitialized = true;
  }
  return store;
};

const useSessionStoreLocal = defineStore('localSessionStore', {
  // convert to a function
  state: (): State => ( {
    myorders: [{ Id: 'Server',invoiceUrl: '', messages: [], showDelete: false, running: { running: false, status: '' },passwordPresented:false , tStamp: new Date() }],
    mylocale: 'ro',
    myfallbackLocale: 'ro',
    _appConfig: null,
    _appConfigTime: null,
    _customerId: '',
    myorderStarted: false,
  }),
  persist: true,
  getters: {
    locale: state => state.mylocale,
    fallbackLocale: state => state.myfallbackLocale,
    // The app cannot function before the config loads and every consumer
    // already assumes it is present — expose it as such instead of forcing
    // a null-check at each of the 15+ call sites.
    appConfig: state => state._appConfig as AppConfig,
    customerId: state => state._customerId,
    hasRunningOrders: (state) => 
      {
        return state.myorders.some(order => (order.running.running === true) && (order.tStamp > new Date(Date.now() - 10 * 60 * 60 * 1000)));
      },
    orderStarted: (state) => state.myorderStarted,
    orders: (state) => [...state.myorders].sort((a, b) => new Date(b.tStamp).getTime() - new Date(a.tStamp).getTime()),
  },
  actions: {
    getMessages(orderId: string) {
      const order = this.myorders.find(order => order.Id === orderId);
      return order ? order.messages.slice(-15).reverse() : [];
    },
    getMessageClasses(message_type: string) {
        switch (message_type) {
          case 'info':
            return 'text-info';
          case 'success':
            return 'text-info';
          case 'error':
            return 'text-danger';
          case 'debug':
            return 'text-primary';
          case 'server-error':
            return 'text-danger';
          case 'server-info':
            return 'text-server';
          case 'server-success':
            return 'text-info';
          case 'server-debug':
            return 'text-info';
          default:
            return '';
        }

    },
    async setLocale(locale: string) {
      if (locale != '')
      {
        this.mylocale = locale;
      }
    },
    async setAppConfig(appConfig: AppConfig | null) {
      //console.error('Setting app config:', appConfig);
      this._appConfig = appConfig;
      this._appConfigTime = appConfig ? Date.now() : null;
    },
    async setCustomerId(customerId: string) {
      this._customerId = customerId;
    },
    async showDelete(orderId: string) {
      const order = this.myorders.find(order => order.Id === orderId);
      if (order) {
        order.showDelete = true;
      }
    },
    async hideDelete(orderId: string) {
      const order = this.myorders.find(order => order.Id === orderId);
      if (order) {
        order.showDelete = false;
      }
    },
    async deleteOrder(orderId: string) {
      const orderIndex = this.myorders.findIndex(order => order.Id === orderId);
      if (orderIndex >= 0) {
        this.myorders.splice(orderIndex, 1);
      }
    },
    async setOrderStarted(orderStarted: boolean) {
      this.myorderStarted = orderStarted;
    },

    async logMessage(message: string | ServerLogMessage, type: string) {
      const tStamp = new Date();
      try
      {
        const orderId = (typeof message === 'string' ? undefined : message.orderId) || 'Server';
        let order = this.myorders.find(order => order.Id === orderId);
        if (!order) {
          order = {
            Id: orderId,
            showDelete: false,
            messages: [],
            invoiceUrl: '',
            running: { status: 'started', running: false },
            tStamp: tStamp, 
            passwordPresented: false
          };
          this.myorders.push(order);
        }

        let finaltype = type;
        const notifications = useNotifications();
        
        if (typeof message == 'string')
        {
          message= `${new Date().toLocaleTimeString()} - ${ getTranslation(message)}`;
          order.messages.push({ message, type: finaltype, tStamp: tStamp });
          notifications.notify(
            {
              message: message,
              overlap: false,
              verticalAlign: 'top',
              horizontalAlign: 'right',
              type: 'info',
              timeout: 5000,
              closeOnClick: true,
              showClose: true
            });
            order.tStamp = tStamp;
        }
        else
        {
          if (message.command)
          {
            
            const old_runing_state = order.running.running? true: false;
            // if command is started we should update a running variable
            order.running.status = message.command;
            
            if (message.command == 'started') 
            {
              this.setOrderStarted(true);
              order.running.running = true;
            }
            else if (message.command == "finished") 
            {
              order.running.running = false;
            }
            else if (message.command == "completed")
            {
              order.running.running = false;
    
            }
            else if (message.command == 'canceled') 
            {
              order.running.running = false;
            }
            else if (message.command == 'failed') 
            {
              order.running.running = false;
            }
            else if (message.command == 'stopped') 
            {
              order.running.running = false;
              
            }
            else if (message.command == 'invoiceUrl')
            {
              if (message.command_data?.invoiceUrl) {
                order.invoiceUrl = message.command_data.invoiceUrl;
              }
            }
            else if (message.command == 'running') 
            {
              order.running.running = true; 
            }
            else if (message.command == 'redirect')
            {
              // go to the payment page if the command is redirect
              const redirectUrl = message.command_data?.url;
              if (redirectUrl)
              {
               
                const url = redirectUrl;
                const newWindow = window.open(url, '_blank');
                if (newWindow === null || typeof(newWindow)=='undefined') {
                  finaltype = 'server-error';
                  message = `Please open the payment page here: {link}`;
                  const data = {link: url};

                  for (const key in data) {
                    if (typeof data[key as keyof typeof data] === 'string' && data[key as keyof typeof data].startsWith('https://')) {
                      data[key as keyof typeof data] = `<a href="${data[key as keyof typeof data]}" target="_blank">Link</a>`;
                    }
                  }
                  const preMessageStage = getTranslation(message, data);
                  message = `${new Date().toLocaleTimeString()} - ${preMessageStage}`;
                  order.messages.push({ message, type: finaltype, tStamp: tStamp });
                  
                } 
              } 
            }
            if (order.running.running == false) this.setOrderStarted(false);
            if (typeof message !== 'string' && message.command_data && message.command_data.message_type) {
             
              const data = message.command_data.data;
              finaltype = message.command_data.message_type;
              if (data)
              {
                // if data object contains objects containing urls then replace url with <a href> tag
                for (const key in data) {
                  if (typeof data[key] === 'string' && data[key].startsWith('https://')) {
                    data[key] = `<a href="${data[key]}" target="_blank">Link</a>`;
                  }
                }
                message =  `${getTranslation(message.command_data.message, data) }`;
              }
              else
              {
                message =  `${getTranslation(message.command_data.message)}`;
              }
              if (message.length > 0) order.messages.push({ message, type: finaltype, tStamp: tStamp });
         
              if (old_runing_state != order.running.running)
              {
                notifications.notify(
                  {
                    message: getTranslation("New order status:")+": "+message,
                    horizontalAlign: 'left',
                    verticalAlign: 'top',
                    type: 'warning',
                  });
                  order.tStamp = tStamp;
              }
            }
            else if (typeof message !== 'string' && message.command && message.command != 'redirect')
            {
              if (old_runing_state != order.running.running)
              {
                finaltype = 'info';
                notifications.notify(
                  {
                    message:getTranslation("New order status:")+": "+getTranslation(message.command),
                    horizontalAlign: 'left',
                    verticalAlign: 'top',
                    type: 'warning',
                  })
                  order.tStamp = tStamp;
              }
            }
            
            if (order.passwordPresented == false && order.running.running == true)
            {
                order.passwordPresented = true;
                useAuthUserStore().getUser().then((useMe: { attributes: Record<string, string | undefined> } | null) => {
                  if (useMe &&  useMe.attributes['custom:tempPassword']) {
                    let message = 'Please write down your temporary password and username to access your balance from another device or at a later time if required! Username: {username} Password: {password}';
                    finaltype = 'server-error';
                   
                    const data = {username: useMe.attributes.email, password: useMe.attributes['custom:tempPassword']};
                    if (data) {
                      // if data object contains objects containing urls then replace url with <a href> tag
                      for (const key in data) {
                        if (data && typeof data[key as keyof typeof data] === 'string' && data[key as keyof typeof data]?.startsWith('https://')) {
                          data[key as keyof typeof data] = `<a href="${data[key as keyof typeof data]}" target="_blank">Link</a>`;
                        }
                      }
                      message = `${getTranslation(message, data)}`;
                    } else {
                      message = `${getTranslation(message)}`;
                    }
                    if (order && message.length > 0) order.messages.push({ message, type: finaltype, tStamp: tStamp });
                  }
                
                });
              }
          }
          else
          {
            order.tStamp = tStamp;
            const data = message.data;
            if (message.message_type) finaltype = message.message_type;
            if (data)
            {
              for (const key in data) {
                if (typeof data[key] === 'string' && data[key].startsWith('https://')) {
                  data[key] = `<a href="${data[key]}" target="_blank">Link</a>`;
                }
              }
              message =  `${ getTranslation(message.message, data) }`;
            }
            else
            {
              message =  `${  getTranslation(message.message)}`;
            }
            if (message.length > 0) order.messages.push({ message: message, type: finaltype, tStamp: tStamp }); 
          }
        }
      }
      catch (error)
      {
        console.warn('Error logging message:', error);
      }
    },
    async clearLogs() { 
      this.myorders = [];
    }
  }
})